// External Dependencies
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtModule } from '@auth0/angular-jwt';
import { NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MissingTranslationHandler, TranslateModule } from '@ngx-translate/core';
import { QRCodeModule } from 'angularx-qrcode';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { ToastrModule } from 'ngx-toastr';
// Routing & Configuration
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
// Feature Modules
import { ClubModule } from './modules/club/club.module';
import { DisplayModule } from './modules/display/display.module';
import { EcashModule } from './modules/ecash/ecash.module';
import { EventsModule } from './modules/events/events.module';
import { FantasyModule } from './modules/fantasy/fantasy.module';
import { GuardianModule } from './modules/guardian/guardian.module';
import { LoyaltyModule } from './modules/loyalty/loyalty.module';
import { PurchaseModule } from './modules/purchase/purchase.module';
import { TicketsModule } from './modules/tickets/tickets.module';
import { UserModule } from './modules/user/user.module';
import { VouchersModule } from './modules/vouchers/vouchers.module';
import { SharedModule } from './shared/shared.module';

// Utilities
import { MomentDateFormatter } from './shared/utilities/moment-date-formatter';
// @fgb/core
import {
  FGBMissingTranslationHandler,
  NotificationLoyaltyType,
  PopulationGuard,
  ProductCodeGuard,
  ProviderName,
  componentVersioningFn,
  jwtTokenGetter,
  provideAppConfig,
  provideTMAuth,
  tmAuthConfig,
  tmOAuthConfig,
} from '@fgb/core';

// Component Library
import { ContactUsModule } from '@fgb/portal-component-library/src/lib/club/contact-us';
import { EarnPointsModule } from '@fgb/portal-component-library/src/lib/club/earn-points';
import { FaqSectionModule } from '@fgb/portal-component-library/src/lib/club/faq-section';
import { TermsAndConditionsModule } from '@fgb/portal-component-library/src/lib/club/terms-and-conditions';
import { DataSourceDirective, DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { CardTokenListModule } from '@fgb/portal-component-library/src/lib/ecash/card-token-list';
import { EcashDescriptionModule } from '@fgb/portal-component-library/src/lib/ecash/ecash-description';
import { AuctionDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-details';
import { AuctionListModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-list';
import { AuctionPanelModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-panel';
import { FeaturedRewardsModule } from '@fgb/portal-component-library/src/lib/loyalty/featured-rewards';
import { MarketplaceDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-details';
import { MarketplaceListModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list';
import { MarketplaceListItemModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list-item';
import { CountryAndRegionsModule } from '@fgb/portal-component-library/src/lib/shared/country-and-regions';
import { ModelMetaDataFormModule } from '@fgb/portal-component-library/src/lib/shared/forms/model-meta-data-form';
import { ErrorsModule } from '@fgb/portal-component-library/src/lib/shared/errors';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { ShortLongDescriptionModule } from '@fgb/portal-component-library/src/lib/shared/short-long-description';
import { AddressBookModule } from '@fgb/portal-component-library/src/lib/user/address-book';

// General Pages
import { AppComponent } from './app.component';
import { ContactUsPageComponent } from './pages/contact-us-page/contact-us-page.component';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
import { ReferAFriendSuccessPageComponent } from './pages/refer-a-friend-page/refer-a-friend-success-page/refer-a-friend-success-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';

// Loyalty Pages
import { AuctionDetailPageComponent } from './pages/auction-detail-page/auction-detail-page.component';
import { AuctionsPageComponent } from './pages/auctions-page/auctions-page.component';
import { EventsPageComponent } from './pages/events-page/events-page.component';
import { LottoDetailPageComponent } from './pages/lotto-detail-page/lotto-detail-page.component';
import { LottoWalletDetailsPageComponent } from './pages/lotto-wallet-details-page/lotto-wallet-details-page.component';
import { LottosPageComponent } from './pages/lottos-page/lottos-page.component';
import { LottosSuccessPageComponent } from './pages/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplaceDetailPageComponent } from './pages/marketplace-detail-page/marketplace-detail-page.component';
import { MarketplacePageComponent } from './pages/marketplace-page/marketplace-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { QuizPageComponent } from './pages/quiz-page/quiz-page.component';
import { RewardsLayoutComponent } from './pages/rewards-layout/rewards-layout.component';
import { RewardsPageComponent } from './pages/rewards-page/rewards-page.component';
import { WalletPageComponent } from './pages/wallet-page/wallet-page.component';
// Polls, Surveys & Trivia (Games)
import {
  FantasyGamesPageComponent,
  FantasyGamesPlayPageComponent,
} from './pages/fantasy-games-page/fantasy-games-page.component';
import { FantasyPlayerSelectPageComponent } from './pages/fantasy-player-select-page/fantasy-player-select-page.component';
import { GamesLayoutComponent } from './pages/games-layout/games-layout.component';
import { GamesPageComponent } from './pages/games-page/games-page.component';
import { PollsPageComponent } from './pages/polls-page/polls-page.component';
// Account Pages
import { AccountDetailsPageComponent } from './pages/account-details-page/account-details-page.component';
import { EditQuestionsPageComponent } from './pages/edit-questions-page/edit-questions-page.component';
import { ActivityPageComponent } from './pages/activity-page/activity-page.component';
import { FacialIdPageComponent } from './pages/facial-id-page/facial-id-page.component';
import { RepresentativePageComponent } from './pages/representative-page/representative-page.component';
import { MembershipPageComponent } from './pages/membership-page/membership-page.component';
// Ecash Pages
import { EcashHistoryPageComponent } from './pages/ecash-history/ecash-history.component';
import { EcashFailurePageComponent } from './pages/ecash-page/ecash-failure-page/ecash-failure-page.component';
import { EcashPageComponent } from './pages/ecash-page/ecash-page.component';
import { EcashResultPageComponent } from './pages/ecash-page/ecash-result-page/ecash-result-page.component';
import { EcashSuccessPageComponent } from './pages/ecash-page/ecash-success-page/ecash-success-page.component';
// Tickets Pages
import { ContactsPageComponent } from './pages/contacts-page/contacts-page.component';
import { ManageTicketHistoryPageComponent } from './pages/manage-ticket-history-page/manage-ticket-history-page.component';
import { ManageTicketsPageComponent } from './pages/manage-tickets-page/manage-tickets-page.component';
import { TicketTransferPageComponent } from './pages/ticket-transfer-page/ticket-transfer-page.component';
import { TicketTransferSuccessPageComponent } from './pages/ticket-transfer-page/ticket-transfer-success-page/ticket-transfer-success-page.component';
import { TicketsPageComponent } from './pages/ticket-transfer-page/tickets-page/tickets-page.component';
import { TransferConfirmPageComponent } from './pages/ticket-transfer-page/transfer-confirm-page/transfer-confirm-page.component';
// Offer Pages
import { OfferDetailsPageComponent } from './pages/offer-details-page/offer-details-page.component';
import { OffersPageComponent } from './pages/offers-page/offers-page.component';
import { SavingsPageComponent } from './pages/savings-page/savings-page.component';
// Partner Offers Pages
import { PartnerOffersPageComponent } from './pages/partner-offers-page/partner-offers-page.component';
import { PartnerOffersDetailPageComponent } from './pages/partner-offers-page/partner-offers-detail-page/partner-offers-detail-page.component';
import { PartnerOffersListComponent } from './pages/partner-offers-page/components/partner-offers-list/partner-offers-list.component';
// Population Pages
import { TestPopHomePageComponent } from './pages/test-pop-home-page/test-pop-home-page.component';
// Portal Products
import { PortalProductFailurePageComponent } from './pages/portal-products-pages/portal-product-failure-page/portal-product-failure-page.component';
import { PortalProductResultsPageComponent } from './pages/portal-products-pages/portal-product-results-page/portal-product-results-page.component';
import { PortalProductSuccessPageComponent } from './pages/portal-products-pages/portal-product-success-page/portal-product-success-page.component';
// Benefits
import { BenefitModule } from './modules/benefit/benefit.module';
import { BenefitsPageComponent } from './pages/benefits-page/benefits-page.component';
// External Cash Transfer
import { CardPageComponent } from './pages/card-page/card-page.component';
import { TransferArenaCashPageComponent } from './pages/transfer-arena-cash-page/transfer-arena-cash-page.component';
import { TransferArenaCashSuccessPageComponent } from './pages/transfer-arena-cash-success-page/transfer-arena-cash-success-page.component';
import { TransferMultipleAccountsPageComponent } from './pages/transfer-multiple-accounts-page/transfer-multiple-accounts-page.component';
// Club Pages
import { LayoutModule } from './modules/layouts/layout.module';
import { EarnPageComponent } from './pages/earn-page/earn-page.component';
import { GuardianPageComponent } from './pages/guardian-page/guardian-page.component';

// Points Management Page
import { PointsManagementPageComponent } from './pages/points-management-page/points-management-page.component';

// Partners Pages
import { CardLinkModule } from '@fgb/portal-component-library/src/lib/cardlinking';
import { AnnouncementsModule } from '@fgb/portal-component-library/src/lib/club/announcements';
import { OfferSearchBarModule } from '@fgb/portal-component-library/src/lib/club/offer-search-bar';
import { CartItemModule } from '@fgb/portal-component-library/src/lib/loyalty/cart-item';
import { CountdownModule } from '@fgb/portal-component-library/src/lib/shared/countdown';
import { SharedSearchBarModule } from '@fgb/portal-component-library/src/lib/shared/shared-search-bar';
import { BadgesPageComponent } from './pages/badges-page/badges-page.component';
import { CardLinkActivatedOffersPageComponent } from './pages/card-link-activated-offers-page/card-link-activated-offers-page.component';
import { CardLinkAffiliatesPageComponent } from './pages/card-link-affiliates-page/card-link-affiliates-page.component';
import { CardLinkAllOffersPageComponent } from './pages/card-link-all-offers-page/card-link-all-offers-page.component';
import { CardLinkOfferDetailsPageComponent } from './pages/card-link-offer-details-page/card-link-offer-details-page.component';
import { CardLinkingPageComponent } from './pages/card-linking-page/card-linking-page.component';
import { CardLinkingSuccessPageComponent } from './pages/card-linking-success-page/card-linking-success-page.component';
import { CartCheckoutPagePortalProductsComponent } from './pages/cart-checkout-page-portal-products/cart-checkout-page-portal-products.component';
import { CartCheckoutPageComponent } from './pages/cart-checkout-page/cart-checkout-page.component';
import { CartCheckoutResultsPageComponent } from './pages/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { FantasyLeaderboardPageComponent } from './pages/fantasy-leaderboard-page/fantasy-leaderboard-page.component';
import { JokesPageComponent } from './pages/jokes-page/jokes-page.component';
import { LeaderboardPageComponent } from './pages/leaderboard-page/leaderboard-page.component';
import { LottosTermsPageComponent } from './pages/lottos-terms-page/lottos-terms-page.component';
import { MarketplaceTermsPageComponent } from './pages/marketplace-terms-page/marketplace-terms-page.component';
import { NotificationManagementPageComponent } from './pages/notification-management-page/notification-management-page.component';
import { PartnerPageComponent } from './pages/partner-page/partner-page.component';
import { PaymentPageComponent } from './pages/payment-page/payment-page.component';
import { ProductsPageComponent } from './pages/products-page/products-page.component';
import { RenewalBonusTransferPageComponent } from './pages/renewal-bonus-transfer-page/renewal-bonus-transfer-page.component';
import { SubscriptionsPageComponent } from './pages/subscriptions-page/subscriptions-page.component';
import { TailgatePageComponent } from './pages/tailgate-page/tailgate-page.component';
import { TicketInvitePageComponentComponent } from './pages/ticket-invite-page-component/ticket-invite-page-component.component';
import { WorksheetPageComponent } from './pages/worksheet-page/worksheet-page.component';
import { ZoomMeetingPageComponent } from './pages/zoom-meeting-page/zoom-meeting-page.component';

import { RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CampaignsModule } from '@fgb/portal-component-library/src/lib/campaigns';
import { NotificationModule } from '@fgb/portal-component-library/src/lib/club/notification';
import {
  ALERT_NOTIFICATION_COMPONENTS,
  AlertNotificationComponentMap,
  mergeAlertNotificationComponentMaps,
} from '@fgb/portal-component-library/src/lib/club/notification/notification-center/alert/alert-notification.config';
import { FeedModule } from '@fgb/portal-component-library/src/lib/feeds';
import { GamesModule } from '@fgb/portal-component-library/src/lib/games';
import { AddPaymentCardDetailModule } from '@fgb/portal-component-library/src/lib/loyalty/add-payment-card-detail';
import {
  BadgeAchievedModule,
  BadgeFeaturedListModule,
  BadgeListItemModule,
  BadgeOverviewListModule,
} from '@fgb/portal-component-library/src/lib/loyalty/badges';
import { MixedLoyaltyListModule } from '@fgb/portal-component-library/src/lib/loyalty/mixed-loyalty';
import { ScorecardDescriptionComponent, ScorecardModule } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { WalletModule } from '@fgb/portal-component-library/src/lib/loyalty/wallet';
import { PreviewModule } from '@fgb/portal-component-library/src/lib/preview';
import { RegistrationAccountVerificationComponent } from '@fgb/portal-component-library/src/lib/registration';
import { CounterModule } from '@fgb/portal-component-library/src/lib/shared/counter';
import { buildPaymentTemplates } from '@fgb/portal-component-library/src/lib/shared/payment';
import { ExpressCheckoutModule } from '@fgb/portal-component-library/src/lib/shared/payment/express-checkout';
import { SocialShareModalModule } from '@fgb/portal-component-library/src/lib/shared/social-share-modal';
import { TicketTransferModule } from '@fgb/portal-component-library/src/lib/ticket-transfer';
import { ClubSelectorModule } from '@fgb/portal-component-library/src/lib/user/club-selector';
import { EditQuestionsModule } from '@fgb/portal-component-library/src/lib/user/edit-questions';
import { WebcamModule } from 'ngx-webcam';
import { AccountVerifiedPageComponent } from 'src/app/pages/account-activation/account-verified-page/account-verified-page.component';
import { ActivateAccountPageComponent } from 'src/app/pages/account-activation/activate-account-page/activate-account-page.component';
import { FeedsPageComponent } from 'src/app/pages/feeds-page/feeds-page/feeds-page.component';
import { GlowPulseModule } from 'src/app/shared/components/glow-pulse';
import { ClubNotificationComponent } from './modules/club/components/club-notification/club-notification.component';
import { TabComponent } from './modules/club/components/games-tab/tab.component';
import { WelcomeComponent } from './modules/club/components/welcome/welcome.component';
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
import { AuctionWalletDetailsPageComponent } from './pages/auction-wallet-details-page/auction-wallet-details-page.component';
import { CampaignDetailsPageComponent } from './pages/campaign-details-page/campaign-details-page.component';
import { CampaignV2DetailsOfferPageComponent } from './pages/campaign-v2-details-offer-page/campaign-v2-details-offer-page.component';
import { CampaignV2DetailsVoucherPageComponent } from './pages/campaign-v2-details-voucher-page/campaign-v2-details-voucher-page.component';
import { FacialRecognitionUploadPageComponent } from './pages/facial-recognition-upload-page/facial-recognition-upload-page.component';
import { FitnessTrackerPageComponent } from './pages/fitness-tracker-page/fitness-tracker-page.component';
import { GamesV2LayoutComponent } from './pages/games-v2-layout/games-v2-layout.component';
import { KidsLinkedPageComponent } from './pages/kids-linked-page/kids-linked-page.component';
import { MarketplaceTransferSuccessPageComponent } from './pages/marketplace-transfer-success-page/marketplace-transfer-success-page.component';
import { MarketplaceWalletPageComponent } from './pages/marketplace-wallet-page/marketplace-wallet-page.component';
import { SubmissionFormComponent } from './pages/outside-portal-game-page/component/submission-form.component';
import { OutsidePortalGamePageComponent } from './pages/outside-portal-game-page/outside-portal-game-page.component';
import { PollsV2PageComponent } from './pages/polls-v2-page/polls-v2-page.component';
import { PredictorV2LeaderboardPageComponent } from './pages/predictor-v2-leaderboard-page/predictor-v2-leaderboard-page.component';
import { PredictorV2PageComponent, PredictorV2PlayComponent } from './pages/predictor-v2-page/predictor-v2-page.component';
import { ReferAFriendPageComponent } from './pages/refer-a-friend-page/refer-a-friend-page.component';
import { FilterModalModule } from './pages/rewards-page/filter-modal';
import { SplitTransactionPageComponent } from './pages/split-transaction-page/split-transaction-page.component';
import { TierLevelPageComponent } from './pages/tier-level-page/tier-level-page.component';
import { TierLevelProgressComponent } from './pages/tier-level-page/tier-level-progress/tier-level-progress.component';
import { TriviaV2LeaderboardPageComponent } from './pages/trivia-v2-leaderboard-page/trivia-v2-leaderboard-page.component';
import { MarketplaceModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace';
import { LottosV2Module } from '@fgb/portal-component-library/src/lib/loyalty/lottos/lottos-v2';
import { TriviaV2PageComponent, TriviaV2PlayComponent } from './pages/trivia-v2-page/trivia-v2-page.component';
import { WalletClaimedPageComponent } from './pages/wallet-claimed-page/wallet-claimed-page.component';
import { MarketplacePageV2Component } from './pages/marketplace-page/marketplace-page-v2/marketplace-page-v2.component';
import { MarketplacePageV1Component } from './pages/marketplace-page/marketplace-page-v1/marketplace-page-v1.component';
import { MarketplaceDetailPageV1Component } from './pages/marketplace-detail-page/marketplace-detail-page-v1/marketplace-detail-page-v1.component';
import { MarketplaceDetailPageV2Component } from './pages/marketplace-detail-page/marketplace-detail-page-v2/marketplace-detail-page-v2.component';
import { MarketplacePurchaseSuccessV1Component } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-v1/marketplace-purchase-success-v1.component';
import { MarketplacePurchaseSuccessV2Component } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-v2/marketplace-purchase-success-v2.component';
import { CartCheckoutPageV1Component } from './pages/cart-checkout-page/cart-checkout-page-v1/cart-checkout-page-v1.component';
import { EventsPageV1Component } from './pages/events-page/events-page-v1/events-page-v1.component';
import { EventsPageV2Component } from './pages/events-page/events-page-v2/events-page-v2.component';
import { UnifiedCheckoutPageComponent } from './pages/unified-checkout-page/unified-checkout-page.component';
import { UnifiedCheckoutSuccessPageComponent } from './pages/unified-checkout-page/unified-checkout-success-page/unified-checkout-success-page.component';
import { UnifiedCheckoutFailurePageComponent } from './pages/unified-checkout-page/unified-checkout-failure-page/unified-checkout-failure-page.component';
import { UnifiedCheckoutResultsPageComponent } from './pages/unified-checkout-page/unified-checkout-results-page/unified-checkout-results-page.component';
import { MemberNetworkPageComponent } from './pages/member-network-page/member-network-page.component';
import { ScorecardsV2PageComponent } from './pages/scorecards-v2-page/scorecards-v2-page.component';
import { ScorecardsV2SubNavComponent } from './pages/scorecards-v2-page/components/scorecards-v2-sub-nav/scorecards-v2-sub-nav.component';
import { ScorecardsV2Module } from '@fgb/portal-component-library/src/lib/loyalty/scorecards/scorecards-v2';

const tmSSOAuthConfig: tmOAuthConfig = {
  clientId: '0d1bc7bb2005.fortress.tbjrc-tbjrc.us',
  club: 'tbjrc',
  clientSecret: '1fa5a94b2eeddec0a6088a0068fdc308ea965f475f0a49bcc32c2ee06179ce19',
  tokenUri: 'https://auth.ticketmaster.com/api/oauth/token',
  loginUri: 'https://am.ticketmaster.com/tbjrc/sso',
  authoriseUri: 'https://auth.ticketmaster.com/as/authorization.oauth2',
};

const tmLoginAuthConfig: tmAuthConfig = {
  clientId: '0d1bc7bb2005.fortress.tbjrc-tbjrc.us',
  authUri: 'https://auth.ticketmaster.com/archtics/sign-in',
  loginUri: 'https://am.ticketmaster.com/tbjrc/sso',
  visualPresents: 'tbjrc',
};

const alertNotificationComponents: Partial<AlertNotificationComponentMap> = {
  [NotificationLoyaltyType.VoucherCodeEntered]: ClubNotificationComponent,
  [NotificationLoyaltyType.ScorecardComplete]: ScorecardDescriptionComponent,
};

@NgModule({
  declarations: [
    // General Pages
    AppComponent,
    MasterPageComponent,
    HomePageComponent,
    LoginPageComponent,
    LoginAccountsPageComponent,
    MaintenancePageComponent,
    TermsPageComponent,
    FaqPageComponent,
    ReferAFriendPageComponent,
    ContactUsPageComponent,
    ReferAFriendSuccessPageComponent,
    NotFoundPageComponent,
    // Loyalty Pages
    RewardsLayoutComponent,
    RewardsPageComponent,
    AuctionsPageComponent,
    LottosPageComponent,
    MarketplacePageComponent,
    MarketplaceTransferSuccessPageComponent,
    EventsPageComponent,
    WalletPageComponent,
    AuctionDetailPageComponent,
    LottoDetailPageComponent,
    MarketplaceDetailPageComponent,
    LottosSuccessPageComponent,
    MarketplacePurchaseSuccessPageComponent,
    LottoWalletDetailsPageComponent,
    AuctionWalletDetailsPageComponent,
    // Polls, Surveys & Trivia Pages (Games)
    GamesLayoutComponent,
    GamesPageComponent,
    PollsPageComponent,
    FantasyGamesPageComponent,
    FantasyPlayerSelectPageComponent,
    // Account Pages
    AccountDetailsPageComponent,
    EditQuestionsPageComponent,
    ActivityPageComponent,
    FacialIdPageComponent,
    RepresentativePageComponent,
    MembershipPageComponent,
    PointsManagementPageComponent,
    ActivateAccountPageComponent,
    AccountVerifiedPageComponent,
    // Ecash Pages
    EcashPageComponent,
    EcashResultPageComponent,
    EcashSuccessPageComponent,
    EcashFailurePageComponent,
    EcashHistoryPageComponent,
    PaymentPageComponent,
    PortalProductResultsPageComponent,
    PortalProductSuccessPageComponent,
    PortalProductFailurePageComponent,
    // Tickets Pages
    TicketsPageComponent,
    ManageTicketsPageComponent,
    TicketTransferPageComponent,
    TransferConfirmPageComponent,
    TicketTransferSuccessPageComponent,
    ContactsPageComponent,
    ManageTicketHistoryPageComponent,
    // Offer Pages
    OffersPageComponent,
    OfferDetailsPageComponent,
    SavingsPageComponent,
    // Partner Offers Pages
    PartnerOffersPageComponent,
    PartnerOffersDetailPageComponent,
    PartnerOffersListComponent,
    // Population Pages
    TestPopHomePageComponent,
    // Guardians Pages
    GuardianPageComponent,
    // External Cash Transfers
    CardPageComponent,
    TransferArenaCashPageComponent,
    TransferMultipleAccountsPageComponent,
    TransferArenaCashSuccessPageComponent,
    // Club Pages
    EarnPageComponent,
    BenefitsPageComponent,
    QuizPageComponent,
    PartnerPageComponent,
    LeaderboardPageComponent,
    LottosTermsPageComponent,
    MarketplaceTermsPageComponent,
    JokesPageComponent,
    WorksheetPageComponent,
    ZoomMeetingPageComponent,
    FantasyLeaderboardPageComponent,
    RenewalBonusTransferPageComponent,
    SubscriptionsPageComponent,
    CartCheckoutPagePortalProductsComponent,
    CartCheckoutPageComponent,
    CartCheckoutSuccessPageComponent,
    CartCheckoutResultsPageComponent,
    NotificationManagementPageComponent,
    TailgatePageComponent,
    ProductsPageComponent,
    BadgesPageComponent,
    CardLinkingPageComponent,
    CardLinkingSuccessPageComponent,
    CardLinkAffiliatesPageComponent,
    CardLinkActivatedOffersPageComponent,
    CardLinkOfferDetailsPageComponent,
    CardLinkAllOffersPageComponent,
    CardLinkActivatedOffersPageComponent,
    TicketInvitePageComponentComponent,
    SplitTransactionPageComponent,
    CampaignDetailsPageComponent,
    CampaignV2DetailsOfferPageComponent,
    CampaignV2DetailsVoucherPageComponent,
    WelcomeComponent,
    // Registration
    RegistrationLayoutPageComponent,
    WalletClaimedPageComponent,
    KidsLinkedPageComponent,
    MarketplaceWalletPageComponent,
    // Games
    TriviaV2PageComponent,
    PredictorV2PageComponent,
    FacialRecognitionUploadPageComponent,
    PollsV2PageComponent,
    GamesV2LayoutComponent,
    TierLevelPageComponent,
    TierLevelProgressComponent,
    FeedsPageComponent,
    TriviaV2PlayComponent,
    PredictorV2PlayComponent,
    TriviaV2LeaderboardPageComponent,
    PredictorV2LeaderboardPageComponent,
    FitnessTrackerPageComponent,
    OutsidePortalGamePageComponent,
    SubmissionFormComponent,
    FantasyGamesPlayPageComponent,
    // Marketplace Versions
    MarketplacePageV1Component,
    MarketplacePageV2Component,
    MarketplaceDetailPageV1Component,
    MarketplaceDetailPageV2Component,
    MarketplacePurchaseSuccessV1Component,
    MarketplacePurchaseSuccessV2Component,
    CartCheckoutPageV1Component,
    EventsPageV1Component,
    EventsPageV2Component,
    UnifiedCheckoutPageComponent,
    UnifiedCheckoutResultsPageComponent,
    UnifiedCheckoutSuccessPageComponent,
    UnifiedCheckoutFailurePageComponent,
    MemberNetworkPageComponent,
    ScorecardsV2PageComponent,
    ScorecardsV2SubNavComponent,
  ],
  imports: [
    // External Modules
    RouterModule,
    CarouselModule,
    AppRoutingModule,
    FormsModule,
    NgbModule,
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    InlineSVGModule.forRoot(),
    QRCodeModule,
    BrowserAnimationsModule,
    EarnPointsModule,
    FaqSectionModule,
    TermsAndConditionsModule,
    AuctionDetailsModule,
    AuctionPanelModule,
    AuctionListModule,
    // Lottos V2
    LottosV2Module,
    ScorecardModule,
    ScorecardsV2Module,
    BadgeListItemModule,
    BadgeOverviewListModule,
    BadgeFeaturedListModule,
    MarketplaceDetailsModule,
    MarketplaceModule,
    ShortLongDescriptionModule,
    MarketplaceListModule,
    MarketplaceListItemModule,
    MixedLoyaltyListModule,
    LoadingModule,
    CountryAndRegionsModule,
    ModelMetaDataFormModule,
    EcashDescriptionModule,
    CardTokenListModule,
    CountdownModule,
    CounterModule,
    TicketTransferModule,
    ErrorsModule,
    EditQuestionsModule,
    FeaturedRewardsModule,
    AddressBookModule,
    WalletModule,
    NotificationModule,
    ContactUsModule,
    CampaignsModule,
    AddPaymentCardDetailModule,
    FilterModalModule,
    BadgeAchievedModule,
    SocialShareModalModule,
    ToastrModule.forRoot({
      maxOpened: 5,
    }),
    JwtModule.forRoot({
      config: {
        allowedDomains: ['http://localhost/portal2/api'],
        tokenGetter: jwtTokenGetter,
      },
    }),
    TranslateModule.forRoot({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FGBMissingTranslationHandler,
      },
    }),
    // Feature Modules
    LayoutModule,
    GuardianModule,
    PurchaseModule,
    EventsModule,
    ClubModule,
    EcashModule,
    PurchaseModule,
    VouchersModule,
    UserModule,
    SharedModule,
    FantasyModule,
    TicketsModule,
    LoyaltyModule,
    BenefitModule,
    DisplayModule,
    DirectivesModule,
    CardLinkModule,
    AnnouncementsModule,
    OfferSearchBarModule,
    SharedSearchBarModule,
    CartItemModule,
    PreviewModule,
    ClubSelectorModule,
    ServiceWorkerModule.register('fgb-service-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    GamesModule,
    WebcamModule,
    DataSourceDirective,
    GlowPulseModule,
    FeedModule,
    TabComponent,
    RegistrationAccountVerificationComponent,
    ExpressCheckoutModule,
  ],
  providers: [
    provideAppConfig({
      dateOptions: {
        LongDateFormat: 'MMM D, yyyy h:mma',
      },
      defaultCountry: 'United States',
      // Force inclusion of payment templates, to force error when upgrading new versions
      paymentTemplates: buildPaymentTemplates(),
      // The provider card payments are taken through
      paymentProvider: ProviderName.FreedomPay,
      // can remove this if no features are enabled to use defaults
      feature: {
        enableDarkMode: false,
      },
      // Handles versioning of portal components
      componentVersions: {
        marketplace: componentVersioningFn('marketplaceVersion', 2, true), // use compontentVersioningFn only in testing, for production use the version number directly
        lotto: 2,
      },
    }),
    provideTMAuth({
      ssoAuth: tmSSOAuthConfig,
      loginAuth: tmLoginAuthConfig,
    }),
    {
      provide: ALERT_NOTIFICATION_COMPONENTS,
      useFactory: () => {
        return mergeAlertNotificationComponentMaps(alertNotificationComponents);
      },
    },
    PopulationGuard,
    ProductCodeGuard,
    { provide: NgbDateParserFormatter, useClass: MomentDateFormatter },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
